import { useContext, useEffect } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import WorkOrdersContext, {
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import { usePreviousValue } from '@atom/hooks/usePreviousValue';
import { Icon, Progress } from '@atom/mui';
import { ArrowLeftIcon, ArrowRightIcon } from '@atom/mui/icons';
import { isWorkLocationFilteringEnabled } from '@atom/utilities/tenantFeatureUtilities';

import WorkOrdersFiltersFormSection from './WorkOrdersFiltersFormSection';
import WorkOrdersFiltersHeader from './WorkOrdersFiltersHeader';
import WorkOrdersFiltersInventorySection from './WorkOrdersFiltersInventorySection';
import WorkOrdersFiltersLocationSection from './WorkOrdersFiltersLocationSection';
import WorkOrdersFiltersTaskSection from './WorkOrdersFiltersTaskSection';
import WorkOrdersFiltersTeamSection from './WorkOrdersFiltersTeamSection';
import WorkOrdersFiltersWorkSection from './WorkOrdersFiltersWorkSection';

import classes from './workOrdersFilters.module.css';

const WorkOrdersFilters = () => {
  const { input, setInput, dispatch, loadingFilters } =
    useContext(WorkOrdersContext);

  const previousInput = usePreviousValue(input);

  useEffect(() => {
    // Collapsing or expanding the drawer shouldn't overwrite unapplied
    // selections in the cart, so only sync the expanded flag in that case.
    const onlyExpandedChanged =
      !R.isNil(previousInput) &&
      previousInput.expanded !== input?.expanded &&
      R.equals(
        R.omit(['expanded'], previousInput),
        R.omit(['expanded'], input),
      );

    dispatch(
      onlyExpandedChanged
        ? {
            type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
            data: { property: 'expanded', value: input.expanded },
          }
        : {
            type: WorkOrdersInputActionTypes.SET_WORK_ORDERS_INPUT,
            data: input,
          },
    );
  }, [input]);

  const expandIcon = input?.expanded ? ArrowLeftIcon : ArrowRightIcon;
  const toggleExpanded = () => {
    setInput({
      ...input,
      expanded: !input?.expanded,
    });
  };

  return (
    <div
      className={clsx(
        classes.filtersContainer,
        !input?.expanded && classes.collapsed,
      )}
    >
      <div
        className={clsx(
          classes.expandButton,
          !input?.expanded && classes.collapsed,
        )}
        onClick={toggleExpanded}
      >
        <Icon icon={expandIcon} />
      </div>
      <WorkOrdersFiltersHeader />
      {loadingFilters ? (
        <div className={classes.filterLoadingContainer}>
          <Progress />
        </div>
      ) : (
        <div className={classes.filtersContent}>
          <WorkOrdersFiltersWorkSection />
          <WorkOrdersFiltersTaskSection />
          {isWorkLocationFilteringEnabled && (
            <WorkOrdersFiltersLocationSection />
          )}
          <WorkOrdersFiltersTeamSection />
          <WorkOrdersFiltersInventorySection />
          <WorkOrdersFiltersFormSection />
        </div>
      )}
    </div>
  );
};

export default WorkOrdersFilters;
